import { useState, useMemo } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import {
    mechanismCategoryLabels,
    mechanismSignalsByCategory,
} from '../../helpers/dataHelpers/mechanismCategories'

// "Werkzame mechanismen" — the "why does it work" layer, benchmarked. Mirrors
// BenchmarkActivitiesSection/Context: a collapsible table (reusing the bd-acttab-*
// styles) where each row is a mechanismecategorie that opens to its concrete
// mechanismen (signals). Both levels are controlled vocabulary, so both carry a
// top-vs-rest %. The caller's own category/signal rows are highlighted. The
// category→signal nesting is reconstructed here from the static taxonomy
// (mechanismSignalsByCategory), so the backend only ships two flat dimension
// blocks. Renders nothing when no category has data (also: old backend → null).
// AI-CONTEXT: See docs/features/benchmark-mechanisms-section.md

const COLUMNS = [
    { key: 'label', label: '', searchable: false, headerClassName: 'bd-acttab-head-label' },
    { key: 'pct', label: 'Inzet bij koplopers', searchable: false, headerClassName: 'bd-acttab-head-pct' },
]

/**
 * @param {object} props
 * @param {boolean} props.open - Whether the category row is expanded
 */
const Chevron = ({ open }) =>
    open ? <KeyboardArrowUpOutlinedIcon fontSize='small' /> : <KeyboardArrowDownOutlinedIcon fontSize='small' />

const toggleInSet = (set, key) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
}

const getItemKey = (item) =>
    item.kind === 'category' ? `cat-${item.key}` : `signal-${item.catKey}-${item.key}`

/**
 * @param {object} props
 * @param {object} props.explanation - msiBenchmark explanation block. Reads
 *   `mechanismCategory` + `mechanismSignal` ({ coverage, rows }) and `callerDims`.
 * @param {boolean} [props.showCallerHighlight=true] - Highlight the caller's own
 *   category/signal rows. Off in template/preview renders.
 */
const BenchmarkMSIMechanismsSection = ({ explanation, showCallerHighlight = true }) => {
    const [expandedCats, setExpandedCats] = useState(() => new Set())

    const categoryBlock = explanation?.mechanismCategory

    const categoriesWithData = useMemo(() => {
        const rows = categoryBlock?.rows || []
        if ((categoryBlock?.coverage?.top || 0) === 0) return []
        return [...rows].sort((a, b) => b.topPct - a.topPct)
    }, [categoryBlock])

    const flattenedRows = useMemo(() => {
        const signalRows = explanation?.mechanismSignal?.rows || []
        const out = []
        categoriesWithData.forEach(row => {
            out.push({ kind: 'category', key: row.key, topPct: row.topPct })
            if (!expandedCats.has(row.key)) return

            const allowed = new Set(mechanismSignalsByCategory[row.key] || [])
            const signals = signalRows
                .filter(s => allowed.has(s.key))
                .sort((a, b) => b.topPct - a.topPct)
            if (signals.length === 0) {
                out.push({ kind: 'empty', key: `empty-${row.key}`, message: 'Geen mechanismen geregistreerd.' })
                return
            }
            signals.forEach(s => out.push({ kind: 'signal', catKey: row.key, key: s.key, topPct: s.topPct }))
        })
        return out
    }, [categoriesWithData, explanation, expandedCats])

    if (!explanation || categoriesWithData.length === 0) return null

    const callerDims = (showCallerHighlight && explanation.callerDims) || {}
    const catCallerSet = new Set(callerDims.mechanismCategory || [])
    const signalCallerSet = new Set(callerDims.mechanismSignal || [])

    const toggleCat = (key) => setExpandedCats(prev => toggleInSet(prev, key))

    const rowClassNameHandler = (item) => {
        if (item.kind === 'category') {
            const isOpen = expandedCats.has(item.key)
            const isCaller = catCallerSet.has(item.key)
            return `bd-acttab-row bd-acttab-row--type${isCaller ? ' bd-acttab-row--caller' : ''}${isOpen ? ' bd-acttab-row--open' : ''}`
        }
        if (item.kind === 'signal') {
            const isCaller = signalCallerSet.has(item.key)
            return `bd-acttab-row bd-acttab-row--subtype${isCaller ? ' bd-acttab-row--caller' : ''}`
        }
        return 'bd-acttab-row bd-acttab-row--empty'
    }

    const rowPropsHandler = (item) => {
        if (item.kind === 'category') {
            return {
                'data-testid': `bd-mechtab-row-cat-${item.key}`,
                'data-row-key': item.key,
                onClick: () => toggleCat(item.key),
            }
        }
        if (item.kind === 'signal') {
            return {
                'data-testid': `bd-mechtab-row-signal-${item.catKey}-${item.key}`,
                'data-row-key': item.key,
            }
        }
        return {}
    }

    const renderRowHandler = (item) => {
        if (item.kind === 'category') {
            const isOpen = expandedCats.has(item.key)
            const label = mechanismCategoryLabels[item.key] || item.key
            return (
                <>
                    <TableCell className='bd-acttab-cell-label'>
                        <Chevron open={isOpen} />
                        <span>{label}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        if (item.kind === 'signal') {
            return (
                <>
                    <TableCell className='bd-acttab-cell-label bd-acttab-cell-label--subtype'>
                        <span>{item.key}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        return (
            <TableCell colSpan={2} className='bd-acttab-cell-empty'>{item.message}</TableCell>
        )
    }

    return (
        <div className='bd-acttab-block'>
            <h2 className='bd-section-heading'>Werkzame mechanismen</h2>
            <p className='bd-section-sub'>
                Wat maakt het werk van de organisaties die boven het benchmark presteren werkzaam? Klik een
                categorie open voor de concrete mechanismen.
            </p>
            <Table
                columns={COLUMNS}
                items={flattenedRows}
                renderRow={renderRowHandler}
                getItemKey={getItemKey}
                getRowClassName={rowClassNameHandler}
                rowProps={rowPropsHandler}
                showSearch={false}
                tableClassName='bd-acttab'
            />
        </div>
    )
}

export default BenchmarkMSIMechanismsSection
